import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/apiClient";
import type { RecurrenceOption, Transaction, TransactionStatus, TransactionType } from "./types";

export interface TransactionFilters {
  accountId?: string;
  categoryId?: string;
  status?: TransactionStatus;
  type?: TransactionType;
  startDate?: string;
  endDate?: string;
}

function toQueryString(filters: TransactionFilters) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function useTransactions(filters: TransactionFilters = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["transactions", filters],
    queryFn: () =>
      apiFetch<{ transactions: Transaction[] }>(`/transactions${toQueryString(filters)}`).then((r) => r.transactions),
    enabled: options.enabled,
  });
}

export interface CreateTransactionInput {
  accountId: string;
  categoryId: string;
  amount: number;
  type: "income" | "expense";
  description: string;
  date: string;
  status: TransactionStatus;
  recurrence: RecurrenceOption;
  installmentTotal?: number;
}

export function useCreateTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTransactionInput) =>
      apiFetch<{ transactions: Transaction[] }>("/transactions", { method: "POST", body: data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["transactions"] });
      qc.invalidateQueries({ queryKey: ["accounts"] });
      qc.invalidateQueries({ queryKey: ["dashboard-summary"] });
      qc.invalidateQueries({ queryKey: ["credit-cards"] });
      qc.invalidateQueries({ queryKey: ["budgets"] });
      qc.invalidateQueries({ queryKey: ["calendar-summary"] });
      qc.invalidateQueries({ queryKey: ["period-summary"] });
    },
  });
}

export function useUpdateTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<{
        accountId: string;
        categoryId: string;
        amount: number;
        description: string;
        date: string;
        status: TransactionStatus;
      }>;
    }) => apiFetch<{ transaction: Transaction }>(`/transactions/${id}`, { method: "PATCH", body: data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["transactions"] });
      qc.invalidateQueries({ queryKey: ["accounts"] });
      qc.invalidateQueries({ queryKey: ["dashboard-summary"] });
      qc.invalidateQueries({ queryKey: ["credit-cards"] });
      qc.invalidateQueries({ queryKey: ["budgets"] });
      qc.invalidateQueries({ queryKey: ["calendar-summary"] });
      qc.invalidateQueries({ queryKey: ["period-summary"] });
    },
  });
}

export interface VoiceParseResult {
  transcript: string;
  amount: number;
  type: "income" | "expense";
  description: string;
  date: string;
  categoryId: string | null;
  categoryName: string | null;
}

export function useParseVoiceTransaction() {
  return useMutation({
    mutationFn: (audioBlob: Blob) => {
      const form = new FormData();
      form.append("audio", audioBlob, "gravacao.webm");
      return apiFetch<VoiceParseResult>("/transactions/parse-voice", { method: "POST", body: form });
    },
  });
}

export function useDeleteTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/transactions/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["transactions"] });
      qc.invalidateQueries({ queryKey: ["accounts"] });
      qc.invalidateQueries({ queryKey: ["dashboard-summary"] });
      qc.invalidateQueries({ queryKey: ["credit-cards"] });
      qc.invalidateQueries({ queryKey: ["budgets"] });
      qc.invalidateQueries({ queryKey: ["calendar-summary"] });
      qc.invalidateQueries({ queryKey: ["period-summary"] });
    },
  });
}
